/**
 * HTTP Cache Service — conditional-GET validators and page manifests, one row
 * per server resource, scoped by organization (see types/http_cache.ts).
 *
 * This is cache, not session: it is cheap to lose (the next refresh fetches in
 * full) and it is forgotten by collection when the rows it describes are
 * removed locally. It lives in its own table so it can be pruned by index and
 * so the session store holds only session state. Its validators and manifests
 * are encrypted at rest like the rows they describe (db.ts).
 */

import Dexie from 'dexie';
import db from '@/db';
import type { HttpCacheEntry, PageManifest } from '../types/http_cache';
import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('HttpCache', LogLevel.DEBUG);


export interface CacheRef {
  organizationId: number;
  /** Resource name, unique within the organization. */
  name: string;
  /** Group the entry is forgotten with; defaults to the name itself. */
  collection?: string;
}

export interface CacheValidators {
  etag: string | null;
  lastModified: string | null;
  manifest?: PageManifest;
}

export function cacheKey(ref: CacheRef): string {
  return `${ref.organizationId}:${ref.name}`;
}

export async function getCacheEntry(ref: CacheRef): Promise<HttpCacheEntry | null> {
  try {
    return await db.httpCache.get(cacheKey(ref)) ?? null;
  } catch (error) {
    logger.error(`Failed to read cache entry ${cacheKey(ref)}:`, error);
    return null;
  }
}

export async function putCacheEntry(ref: CacheRef, validators: CacheValidators): Promise<void> {
  const entry: HttpCacheEntry = {
    key: cacheKey(ref),
    organization_id: ref.organizationId,
    collection: ref.collection ?? ref.name,
    name: ref.name,
    etag: validators.etag,
    last_modified: validators.lastModified,
    ...(validators.manifest ? { manifest: validators.manifest } : {}),
    updated_at: new Date().toISOString(),
  };
  try {
    await db.httpCache.put(entry);
  } catch (error) {
    logger.error(`Failed to write cache entry ${entry.key}:`, error);
  }
}

export async function deleteCacheEntry(ref: CacheRef): Promise<void> {
  try {
    await db.httpCache.delete(cacheKey(ref));
  } catch (error) {
    logger.error(`Failed to delete cache entry ${cacheKey(ref)}:`, error);
  }
}

/**
 * Drop every entry of one organization, paged collections and single resources
 * alike, so the next refresh fetches everything in full. Settings > "Sync now"
 * uses this as the escape hatch when data looks out of date: no stored
 * validator survives to buy a 304. Rows of other organizations are untouched.
 */
export async function forgetOrganizationCache(organizationId: number): Promise<void> {
  try {
    await db.httpCache
      .where('[organization_id+collection]')
      .between([organizationId, Dexie.minKey], [organizationId, Dexie.maxKey])
      .delete();
  } catch (error) {
    logger.error(`Failed to forget cache for organization ${organizationId}:`, error);
  }
}

/**
 * Drop every entry of a collection in one organization — e.g. the pages of a
 * client's documents once the client is removed locally. `except` keeps the
 * named entries, which is how a walk sweeps the pages past its end.
 */
export async function forgetCollection(organizationId: number, collection: string, { except = [] as Iterable<string> } = {}): Promise<void> {
  const keep = new Set(except);
  try {
    await db.httpCache
      .where('[organization_id+collection]').equals([organizationId, collection])
      .filter(entry => !keep.has(entry.name))
      .delete();
  } catch (error) {
    logger.error(`Failed to forget cache collection ${organizationId}:${collection}:`, error);
  }
}
